import React, { useState, useEffect } from "react";
import { DatePicker, Select, Button, Table, Modal, message } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  useFetchAttendanceReport,
  useFetchEmployeeOptions,
} from "../hooks/useFetch";
import { formatMinutes, ShiftTag, StatusTags, InOutCell, onReportHeaderCell } from "../utils";

// รายงานการเข้า-ออกงาน (รายวัน) — เลือกดูทีละวัน ทุกคนของวันนั้น
const AttendanceReportPage = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [rows, setRows] = useState([]);
  const [breakDetail, setBreakDetail] = useState(null); // แถวที่กดดูรายละเอียดพัก

  const { data: employeeOptions = [], isLoading: isLoadingEmployees } =
    useFetchEmployeeOptions();
  const { mutateAsync: fetchReport, isLoading } = useFetchAttendanceReport();

  const handleSearch = async (
    date = selectedDate,
    userId = selectedUserId,
    jobType = selectedJobType,
    shift = selectedShift
  ) => {
    try {
      const d = date.format("YYYY-MM-DD");
      const { result } = await fetchReport({
        startDate: d,
        endDate: d,
        userId: userId || undefined,
        jobType: jobType || undefined,
        shift: shift || undefined,
      });
      setRows(result || []);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "ดึงรายงานการเข้า-ออกงานไม่สำเร็จ"
      );
    }
  };

  // โหลดครั้งแรก = วันนี้
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // เลื่อนวัน ก่อนหน้า/ถัดไป แล้วโหลดใหม่ทันที
  const shiftDay = (diff) => {
    const d = selectedDate.add(diff, "day");
    setSelectedDate(d);
    handleSearch(d);
  };

  const columns = [
    {
      title: "พนักงาน",
      dataIndex: "userName",
      key: "userName",
      onHeaderCell: onReportHeaderCell,
      render: (name) => <span style={{ fontWeight: 600 }}>{name}</span>,
      sorter: (a, b) => a.userName.localeCompare(b.userName),
    },
    {
      title: "กะ",
      key: "shift",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => <ShiftTag shift={r.shift} shiftName={r.shiftName} shiftTime={r.shiftTime} />,
      filters: [
        { text: "Day", value: "2" },
        { text: "Night", value: "3" },
        { text: "Afternoon", value: "4" },
        { text: "All Day", value: "1" },
      ],
      onFilter: (value, r) => r.shift === value,
    },
    {
      title: "เข้า → ออก",
      key: "inout",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => <InOutCell record={r} />,
    },
    {
      title: "ชม.ทำงาน (หักพัก)",
      key: "workMinutes",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => <span style={{ fontWeight: 600 }}>{formatMinutes(r.workMinutes)}</span>,
      sorter: (a, b) => (a.workMinutes || 0) - (b.workMinutes || 0),
    },
    {
      title: "พัก (หักสิทธิ์ 1 ชม.)",
      key: "breakMinutes",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) =>
        r.breakCount > 0 ? (
          <div>
            {/* ตัวเลขหลัก = พักสุทธิ (หักสิทธิ์ 1 ชม.แล้ว) — คือ "พักยิบย่อย" ที่เกินสิทธิ์จริง ๆ */}
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setBreakDetail(r)}>
              {formatMinutes(r.breakMinutesNet)} ({r.breakCount} รอบ)
            </Button>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              รวม {formatMinutes(r.breakMinutes)}
              {r.breakCount > 1 && ` · เฉลี่ย ${r.breakAvgMinutes} น.`}
            </div>
            {r.breakOverMinutes > 0 && (
              // พักรวดเดียวเกิน 1 ชม. → ส่วนที่เกินถูกนับเป็นสายไปแล้ว
              <div style={{ fontSize: 12, color: "#cf1322" }}>
                พักเกินสิทธิ์ {formatMinutes(r.breakOverMinutes)}
              </div>
            )}
          </div>
        ) : r.status === "absent" ? (
          "-"
        ) : (
          <span style={{ color: "#8c8c8c" }}>ไม่มีพัก</span>
        ),
    },
    {
      // ===== ข้อมูลการทำเคส (เฉพาะ Panel(Output) — ฝั่ง Input ไม่มีการกด Start) =====
      title: "เคสที่ปิด",
      dataIndex: "caseCount",
      key: "caseCount",
      align: "center",
      width: 90,
      onHeaderCell: onReportHeaderCell,
      render: (v) => (v ? v : <span style={{ color: "#bbb" }}>-</span>),
    },
    {
      title: "ทำงานจริงถึง",
      key: "lastCaseAt",
      align: "center",
      width: 150,
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => {
        if (!r.lastCaseAt) return <span style={{ color: "#bbb" }}>-</span>;
        const idle = r.idleBeforeOutMinutes;
        return (
          <div>
            <div>{String(r.lastCaseAt).slice(11, 16)}</div>
            {idle != null && (
              // ตอกออกแล้วแต่เคสสุดท้ายจบไปนานแล้ว = ช่วงท้ายกะที่ไม่ได้ทำอะไร
              <div style={{ fontSize: 12, color: idle >= 60 ? "#cf1322" : "#8c8c8c" }}>
                ว่างก่อนออก {formatMinutes(idle)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "ว่างก่อนเริ่มเคส",
      key: "idle",
      align: "center",
      width: 150,
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => {
        if (r.idleMinutes == null) return <span style={{ color: "#bbb" }}>-</span>;
        return (
          <div>
            <div>{formatMinutes(r.idleMinutes)}</div>
            <div style={{ fontSize: 12, color: "#8c8c8c" }}>
              เฉลี่ย {r.idleAvgMinutes} นาที/เคส
            </div>
            {r.idleSkipped > 0 && (
              // เคสค้างข้ามกะ ค่าจะเพี้ยน เลยไม่เอามาเฉลี่ย แต่บอกให้รู้ว่าตัดไปกี่เคส
              <div style={{ fontSize: 11, color: "#bbb" }}>
                ไม่นับ {r.idleSkipped} เคส
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "สถานะ",
      key: "status",
      align: "center",
      onHeaderCell: onReportHeaderCell,
      render: (_, r) => <StatusTags record={r} />,
      filters: [
        { text: "ปกติ", value: "done" },
        { text: "กำลังทำงาน", value: "working" },
        { text: "ลืมตอกออก", value: "no_out" },
        { text: "ไม่ได้ตอก", value: "absent" },
        { text: "วันหยุด", value: "holiday" },
        { text: "ลา", value: "leave" },
      ],
      onFilter: (value, r) => r.status === value,
    },
  ];

  return (
    <div style={{ padding: 16, background: "#f5f5f5", minHeight: "100vh", marginLeft: 16 }}>
      <div style={{ marginBottom: 20 }}>
        <h1
          style={{
            margin: 0,
            fontSize: "2rem",
            fontWeight: "bold",
            color: "#212121",
            letterSpacing: "0.5px",
            lineHeight: 1.2,
          }}
        >
          รายงานการเข้า-ออกงาน (รายวัน)
        </h1>
        <p style={{ margin: "8px 0 0", color: "#666" }}>
          ดูการเข้า-ออกงาน ออนไลน์/ออฟไลน์ และการพัก ของพนักงานทุกคนในแต่ละวัน
        </p>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <div className="flex gap-3 items-center flex-wrap">
          <Button icon={<LeftOutlined />} onClick={() => shiftDay(-1)} />
          <DatePicker
            value={selectedDate}
            onChange={(d) => {
              if (!d) return;
              setSelectedDate(d);
              handleSearch(d);
            }}
            format="DD/MM/YYYY"
            allowClear={false}
          />
          <Button
            icon={<RightOutlined />}
            disabled={selectedDate.isSame(dayjs(), "day")}
            onClick={() => shiftDay(1)}
          />
          <Select
            className="w-40"
            placeholder="ตำแหน่งทั้งหมด"
            allowClear
            options={[
              { value: "1", label: "Admin(Input)" },
              { value: "2", label: "Panel(Output)" },
            ]}
            value={selectedJobType}
            onChange={(v) => {
              setSelectedJobType(v || null);
              handleSearch(selectedDate, selectedUserId, v || null, selectedShift);
            }}
          />
          <Select
            className="w-32"
            placeholder="กะทั้งหมด"
            allowClear
            options={[
              // ต้องตรงกับ SHIFT_CONFIG ฝั่ง backend (BackEnd/src/utils/shiftUtils.js)
              { value: "1", label: "All Day" },
              { value: "2", label: "Day" },
              { value: "3", label: "Night" },
              { value: "4", label: "Afternoon" },
            ]}
            value={selectedShift}
            onChange={(v) => {
              setSelectedShift(v || null);
              handleSearch(selectedDate, selectedUserId, selectedJobType, v || null);
            }}
          />
          <Select
            className="w-64"
            placeholder="พนักงานทั้งหมด"
            allowClear
            showSearch
            optionFilterProp="label"
            loading={isLoadingEmployees}
            options={employeeOptions}
            value={selectedUserId}
            onChange={(v) => {
              setSelectedUserId(v || null);
              handleSearch(selectedDate, v || null, selectedJobType, selectedShift);
            }}
          />
          <Button type="primary" loading={isLoading} onClick={() => handleSearch()}>
            ค้นหา
          </Button>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, padding: 16 }}>
        <Table
          rowKey={(r) => `${r.userId}-${r.date}`}
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          bordered
          scroll={{ x: "max-content" }}
          rowClassName={(r) => (r.status === "absent" ? "opacity-60" : "")}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (t) => `ทั้งหมด ${t} รายการ`,
          }}
        />
      </div>
      <style>{`.ant-table-tbody > tr:hover > td { background-color: #e8f4f8 !important; }`}</style>

      {/* รายละเอียดพัก */}
      <Modal
        title={
          breakDetail
            ? `รายละเอียดพัก — ${breakDetail.userName} (${dayjs(breakDetail.date).format("DD/MM/YYYY")})`
            : ""
        }
        open={!!breakDetail}
        onCancel={() => setBreakDetail(null)}
        footer={null}
      >
        {breakDetail && (
          <>
            <p style={{ marginBottom: 12 }}>
              พักรวม <strong>{formatMinutes(breakDetail.breakMinutes)}</strong> ({breakDetail.breakCount} รอบ)
            </p>
            <Table
              columns={[
                { title: "รอบที่", key: "no", render: (_, __, i) => i + 1, width: 70 },
                { title: "ออฟไลน์", dataIndex: "start", key: "start", render: (v) => v.slice(11, 19) },
                { title: "กลับมาออนไลน์", dataIndex: "end", key: "end", render: (v) => v.slice(11, 19) },
                { title: "ระยะเวลา", dataIndex: "minutes", key: "minutes", render: (m) => formatMinutes(m) },
              ]}
              dataSource={breakDetail.breaks}
              rowKey={(b) => b.start}
              pagination={false}
              size="small"
              bordered
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default AttendanceReportPage;
